//@ts-nocheck
import React, { FC, useState, useEffect } from 'react';
import BeerXchangeApi from '../services/BeerXchangeApi';
import UntappdApi from '../services/UntappdApi';
import { UntappdUser } from '../types/model/untappdUser';
import { BeerXchangeUser } from '../types/model/beerXchangeUser';
import {
  UNTAPPD_ACCESS_TOKEN_STORAGE_KEY,
  LOGGED_IN_USER_STORAGE_KEY,
  LOGGED_IN_UNTAPPD_USER_STORAGE_KEY,
} from '../constants';

type LoginContextType = {
  hasLoadedAuth: boolean;
  loginFailed: boolean;
  loggedInUntappdUser?: UntappdUser;
  loggedInUser?: BeerXchangeUser;
  untappdAccessToken?: string;
  isLoggedIn(): boolean;
  receiveCode: (untappdCode: string) => void;
  logInUser(user: object): void;
  logOutUser(): void;
  beginLoadingAuth(): void;
};

export const LoginContext = React.createContext<Partial<LoginContextType>>({
  hasLoadedAuth: false,
  loginFailed: false,
  loggedInUntappdUser: localStorage.getItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY))
    : undefined,
  untappdAccessToken: localStorage.getItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY)
    ? localStorage.getItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY)
    : undefined,
  loggedInUser: localStorage.getItem(LOGGED_IN_USER_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LOGGED_IN_USER_STORAGE_KEY))
    : undefined,
});

export const LoginProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const untappdApi = new UntappdApi();

  const [state, setState] = useState<Partial<LoginContext>>({
    loginFailed: false,
    untappdAccessToken: localStorage.getItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY)
      ? localStorage.getItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY)
      : undefined,
    loggedInUntappdUser: localStorage.getItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY)
      ? JSON.parse(localStorage.getItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY))
      : undefined,
    loggedInUser: localStorage.getItem(LOGGED_IN_USER_STORAGE_KEY)
      ? JSON.parse(localStorage.getItem(LOGGED_IN_USER_STORAGE_KEY))
      : undefined,
  });

  const isLoggedIn = () => {
    return state.loggedInUser !== undefined;
  };

  const logInWithAccessToken = (token: string) => {
    setState((state) => ({ ...state, untappdAccessToken: token }));
    try {
      untappdApi
        .getUser(token)
        .then((response) => {
          logInUser(response.response.user!, token);
        })
        .catch((error: any) => {
          logOutUser();
        });
    } catch (error) {
      setState((state) => ({ ...state, loginFailed: true }));
      logOutUser();
    }
  };

  const receiveCode = (untappdCode: string) => {
    untappdApi
      .authorizeUser(untappdCode)
      .then((response) => {
        storeLoginDataData(response.response.access_token);
        logInWithAccessToken(response.response.access_token);
      })
      .catch((error: any) => {
        setState((state) => ({ ...state, loginFailed: true }));
        logOutUser();
      });
  };

  const logInUser = (user: UntappdUser, token: String) => {
    const beerXchangeApi = new BeerXchangeApi(token, user.id);
    setState((state) => ({ ...state, loggedInUntappdUser: user }));
    localStorage.setItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY, JSON.stringify(user));
    beerXchangeApi
      .getUserInfo(user.user_name)
      .then((response) => {
        setState((state) => ({ ...state, loggedInUser: response, hasLoadedAuth: true }));
        localStorage.setItem(LOGGED_IN_USER_STORAGE_KEY, JSON.stringify(response));
      })
      .catch((error: any) => {
        setState((state) => ({ ...state, loginFailed: true }));
        logOutUser();
      });
  };

  const logOutUser = () => {
    setState((state) => ({
      ...state,
      untappdAccessToken: undefined,
      loggedInUntappdUser: undefined,
      hasLoadedAuth: true,
      loggedInUser: undefined,
    }));
    localStorage.removeItem('collectionFilter');
    localStorage.removeItem('collectionPredicate');
    localStorage.removeItem('collectionReverse');
    localStorage.removeItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(LOGGED_IN_USER_STORAGE_KEY);
    localStorage.removeItem(LOGGED_IN_UNTAPPD_USER_STORAGE_KEY);
  };

  const storeLoginDataData = async (token: string) => {
    localStorage.setItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY, token);
  };

  const beginLoadingAuth = () => {
    setState((state) => ({
      ...state,
      hasLoadedAuth: false,
    }));
  };

  useEffect(() => {
    if (state.loggedInUser === undefined) {
      const token = localStorage.getItem(UNTAPPD_ACCESS_TOKEN_STORAGE_KEY);
      token ? logInWithAccessToken(token) : logOutUser();
      return () => {};
    }
  }, [state.loggedInUser]);

  return (
    <LoginContext.Provider
      value={{
        ...state,
        beginLoadingAuth,
        isLoggedIn,
        receiveCode,
        logOutUser,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};
